import moment from 'moment';

const parseUtc = (dateString: string) => {
    const nonIsoBackend = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
    return nonIsoBackend.test(dateString)
        ? moment.utc(dateString, 'YYYY-MM-DD HH:mm:ss', true)
        : moment.utc(dateString);
};

/**
 * Format a UTC date string to display in user's local timezone
 * Format: "YYYY-MM-DD" (e.g., "2025-10-16")
 * * @param dateString - UTC date string from backend
 * @returns Formatted date string in local timezone
 */
export const formatDateShort = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    return parseUtc(dateString).local().format('YYYY-MM-DD');
};

/**
 * Format a UTC date string to display date and time in user's local timezone
 * Format: "YYYY-MM-DD HH:mm:ss" (e.g., "2025-10-16 14:30:45")
 * * @param dateString - UTC date string from backend
 * @returns Formatted date and time string in local timezone
 */
export const formatDateTimeFull = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    return parseUtc(dateString).local().format('YYYY-MM-DD HH:mm:ss');
};

/**
 * Format a UTC date string to display date and time without seconds
 * Format: "YYYY-MM-DD HH:mm" (e.g., "2025-10-16 14:30")
 * * @param dateString - UTC date string from backend
 * @returns Formatted date and time string without seconds in local timezone
 */
export const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    return parseUtc(dateString).local().format('YYYY-MM-DD HH:mm');
};

/**
 * Format a UTC date string to compact format (ISO-like)
 * Format: "YYYY-MM-DD" (e.g., "2025-10-16")
 * * @param dateString - UTC date string from backend
 * @returns Formatted date string in local timezone
 */
export const formatDateCompact = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    return parseUtc(dateString).local().format('YYYY-MM-DD');
};

/**
 * Format a UTC date string to time only
 * Format: "HH:mm:ss" (e.g., "14:30:45")
 * * @param dateString - UTC date string from backend
 * @returns Formatted time string in local timezone
 */
export const formatTimeOnly = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    return parseUtc(dateString).local().format('HH:mm:ss');
};

/**
 * Format a UTC date string to relative time (e.g., "2 hours ago", "3 days ago")
 * * @param dateString - UTC date string from backend
 * @returns Relative time string
 */
export const formatRelativeTime = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    return parseUtc(dateString).local().fromNow();
};

/**
 * Convert a local Date object to UTC ISO string for API requests
 * Use this when sending dates to the backend
 * * @param date - Local Date object
 * @returns UTC ISO string
 */
export const convertLocalDateToUTC = (date: Date | null | undefined): string | null => {
    if (!date) return null;
    return date.toISOString();
};

/**
 * Convert a date to UTC start of day (00:00:00 UTC)
 * * @param date - Date object
 * @returns ISO string at 00:00:00 UTC
 */
export const convertDateToUTCStartOfDay = (date: Date): string => {
    const utcDate = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
    );
    return utcDate.toISOString();
};

/**
 * Get current date/time in ISO format for API requests
 * * @returns Current date/time in ISO format
 */
export const getCurrentISODate = (): string => {
    return convertLocalDateToUTC(new Date()) ?? '';
};

/**
 * Parse a date string and convert to moment object in local timezone
 * Useful when you need to perform date operations
 * * @param dateString - UTC date string from backend
 * @returns Moment object in local timezone
 */
export const parseDateToLocal = (dateString: string | null | undefined): moment.Moment | null => {
    if (!dateString) return null;
    return parseUtc(dateString).local();
};

/**
 * Format a date for display in a specific locale format
 * Uses browser's locale settings
 * * @param dateString - UTC date string from backend
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string according to browser locale
 */
export const formatDateLocale = (
    dateString: string | null | undefined,
    options?: Intl.DateTimeFormatOptions
): string => {
    if (!dateString) return 'N/A';
    // Prefer parseUtc for consistency (to avoid deprecat4ion warnings on non-ISO)
    const parsed = parseUtc(dateString).local().toDate();
    const defaultOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    };
    return parsed.toLocaleString(undefined, options || defaultOptions);
};

/**
 * Check if a date string is valid
 * * @param dateString - Date string to validate
 * @returns True if valid, false otherwise
 */
export const isValidDate = (dateString: string | null | undefined): boolean => {
    if (!dateString) return false;
    
    const datePattern = /(\d{4}-\d{2}-\d{2})|(\d{2}\/\d{2}\/\d{4})|(\d{2}-\d{2}-\d{4})|(\d{4}\/\d{2}\/\d{2})|T\d{2}:\d{2}/;
    if (!datePattern.test(dateString)) return false;
    
    return moment(dateString).isValid();
};

/**
 * Get the difference between two dates in human-readable format
 * * @param startDate - Start date string (UTC)
 * @param endDate - End date string (UTC)
 * @returns Human-readable duration (e.g., "2 hours 30 minutes")
 */
export const getDateDifference = (startDate: string | null | undefined, endDate: string | null | undefined): string => {
    if (!startDate || !endDate) return 'N/A';

    const start = moment.utc(startDate);
    const end = moment.utc(endDate);
    const duration = moment.duration(end.diff(start));

    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();

    const parts: string[] = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0 && days === 0) parts.push(`${seconds}s`);

    return parts.length > 0 ? parts.join(' ') : '0s';
};

/**
 * Get current date as moment object
 */
export const getCurrentDate = () => moment();

/**
 * Check if a date string is before a comparison date
 * @param dateString - Date string to check
 * @param compareDate - Date to compare against (moment object or string)
 */
export const isDateBefore = (dateString: string | null | undefined, compareDate: any): boolean => {
    if (!dateString) return false;
    return parseUtc(dateString).isBefore(compareDate);
};
